import { useContext, useState } from 'react';
import { Box } from '@mui/material';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { SttForm } from '../../sporttia/SttForm';
import { SttDialog } from '../../sporttia/dialogs/SttDialog';
import { AppContext } from '../../sporttia/AppContext';
import { useInteractionsFiles, useLoader } from '../../lib/hooks';

type Q60Params = {
    codeTramitter: number;
    presenterEntity: string;
    presenterOffice: number;
    paidIni: string;
    paymentDate: string;
    ccc: string | null;
    collectorEntity: number;
    collectorOffice: number;
    paidEnd: string;
    validityDate: string;
    taxCode: number;
};

type Q60DownloadDialogProps = {
    open?: boolean;
    onClose: () => void;
    filters?: Record<string, unknown>;
};

type FormChangeEvent = {
    name: string | undefined;
    value: unknown;
};

type DownloadStatus = 'idle' | 'loading' | 'success';

type ErrorResponse = {
    error: {
        errors?: Array<{
            dni?: boolean;
            fullname?: boolean;
            userId?: string;
        }>;
        msg?: string;
    };
};

export default function Q60DownloadDialog({
    open = false,
    onClose,
    filters = {},
}: Q60DownloadDialogProps): React.ReactElement {
    const cxt = useContext(AppContext)!;
    const { t } = useTranslation();
    const { downloadFile } = useInteractionsFiles();
    const [downloadStatus, setDownloadStatus] =
        useState<DownloadStatus>('idle');
    const today = new Date().toISOString().split('T')[0] ?? '';
    const [q60Params, setQ60Params] = useState<Q60Params>({
        codeTramitter: 30820,
        presenterEntity: '0081',
        presenterOffice: 1045,
        paidIni: today,
        paymentDate: today,
        ccc: null,
        collectorEntity: 81,
        collectorOffice: 1045,
        paidEnd: today,
        validityDate: today,
        taxCode: 35,
    });

    const downloadQ60 = (): void => {
        downloadFile(
            `/scs/${cxt?.sc?.id}/payments/q60mod2`,
            'application/pdf',
            filters,
            undefined,
            'POST',
            { period: q60Params.paidEnd },
            (response: unknown) => {
                const errorResponse = response as ErrorResponse;
                const { errors } = errorResponse.error;
                if (errors && errors.length >= 1) {
                    const formattedErrors = {
                        dniError:
                            errors && errors.find((error: any) => error.dni)
                                ? 'No tiene dni asignado.'
                                : '',
                        fullnameError: errors.find(
                            (error: any) => error.fullname,
                        )
                            ? 'No tiene nombre completo asignado.'
                            : '',
                        userId: errors?.[0]?.userId ?? '',
                    };
                    cxt?.showMessage(
                        'E',
                        `El usuario con id ${formattedErrors.userId ? formattedErrors.userId : ''}: ${formattedErrors.fullnameError} ${formattedErrors.dniError}`,
                    );
                } else {
                    cxt?.showMessage('E', `${errorResponse.error.msg} }`);
                }

                setDownloadStatus('success');
            },
            () => {
                setDownloadStatus('success');
            },
        );
    };

    /**
     * Handle form.
     */
    const handleForm = ({ name, value }: FormChangeEvent): void => {
        if (!name) return;
        setQ60Params({ ...q60Params, [name]: value });
    };

    const [, loader] = useLoader([downloadStatus]);

    return (
        <SttDialog
            maxWidth="md"
            open={open}
            onClose={onClose}
            title="Cuaderno 60"
            content={
                <>
                    {loader}
                    <Paper>
                        <Box p={2}>
                            <SttForm
                                elements={[
                                    {
                                        xs: 4,
                                        caption: t('DateEnd'),
                                        name: 'paidEnd',
                                        type: 'date',
                                        value: q60Params.paidEnd,
                                    },
                                ]}
                                onChangeForm={handleForm}
                            />
                        </Box>
                    </Paper>
                </>
            }
            buttons={[
                {
                    caption: t('Generate'),
                    type: 'accept',
                    onClick: () => {
                        setDownloadStatus('loading');
                        downloadQ60();
                    },
                },
                {
                    caption: t('Close'),
                    onClick: () => {
                        onClose();
                        setDownloadStatus('success');
                    },
                },
            ]}
        />
    );
}
